import React, { useCallback, useMemo } from 'react';
import remove from 'lodash/remove';
import sortBy from 'lodash/sortBy';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { DO_NOT_ANIMATE_WIDTH, RESTRICTED_GROUP, SUGGESTED_GROUP } from '../../constants.tsx';
import type { AvailableLayoutItemsListProps, LayoutItemsFilter } from '../../types.tsx';
import {
	type AccessControl,
	type AvailableLayoutItemGroup,
	FIELD_LAYOUT_ITEM_TYPE,
	type InactiveLayoutItem,
	PANEL_LAYOUT_ITEM_TYPE,
} from '../../types/layout-items.tsx';
import messages from './messages.tsx';

type OnRenderLayoutItemArgs = {
	FieldComponent: AvailableLayoutItemsListProps['FieldComponent'];
	PanelComponent: AvailableLayoutItemsListProps['PanelComponent'];
	project: AvailableLayoutItemsListProps['project'];
	getIndexAfterUnsortableFields: AvailableLayoutItemsListProps['getIndexAfterUnsortableFields'];
	highlightRegExp: null | RegExp;
};

export type AvailableLayoutItemGroupWithName = AvailableLayoutItemGroup & {
	name: string;
};

/**
 * Inactive fields width happens to be in `style.width` already and is correct because there are no paddings or margins.
 */
export const getItemWidth = (style: unknown): number | typeof DO_NOT_ANIMATE_WIDTH => {
	// @ts-expect-error - TS2571 - Object is of type 'unknown'.
	if (style != null && typeof style.width === 'number') {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		return style.width;
	}
	return DO_NOT_ANIMATE_WIDTH;
};

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const endSearchInteraction = (): void => {};

/**
 * Format the `groupId` in the provided {@link AvailableLayoutItemGroup} into a user-readable name, sort the layout
 * groups, and filters out empty groups.
 * We do not sort items inside a group because we want to keep their order for "load more" functionality
 */
export const useAvailableLayoutItemGroups = (
	filter: LayoutItemsFilter,
	itemGroups: ReadonlyArray<AvailableLayoutItemGroup>,
	returnAllGroups = false,
): AvailableLayoutItemGroupWithName[] => {
	const { formatMessage } = useIntl();
	return useMemo(() => {
		const uiGroups = itemGroups.map(({ groupId, ...rest }) => ({
			...rest,
			groupId,
			name: formatMessage(messages[groupId]),
		}));
		const [suggestedGroup] = remove(uiGroups, ({ groupId }) => groupId === SUGGESTED_GROUP);
		const [restrictedGroup] = remove(uiGroups, ({ groupId }) => groupId === RESTRICTED_GROUP);

		if (
			!returnAllGroups &&
			!filter.name &&
			suggestedGroup &&
			(suggestedGroup.items.length > 0 || suggestedGroup.hasNextPage)
		) {
			// if there is no filter, and we have some suggested fields
			// then we only return suggested fields and ignore all the other groups
			return [suggestedGroup];
		}

		// Sort the rest of the groups by name
		const sortedGroups = sortBy(uiGroups, ({ name }) => name);
		if (suggestedGroup) {
			sortedGroups.unshift(suggestedGroup);
		}

		if (restrictedGroup) {
			sortedGroups.unshift(restrictedGroup);
		}

		return sortedGroups.filter((group) => group.items.length > 0 || group.hasNextPage);
	}, [itemGroups, filter, formatMessage, returnAllGroups]);
};

export const getFieldsFilterDisabledReason = (
	isGlobalAdmin: boolean,
	accessControl: AccessControl | null,
): MessageDescriptor | undefined => {
	if (
		isGlobalAdmin ||
		!accessControl ||
		(accessControl.userHasEditLayoutPermission && accessControl.isScreenIsolated)
	) {
		return undefined;
	}

	const screenNotIsolatedMessageValue = fg('jira-issue-terminology-refresh-m3')
		? messages.screenNotIsolatedMessageIssueTermRefresh
		: messages.screenNotIsolatedMessage;

	const userDoesntHaveEditLayoutPermissionMessageValue = fg('jira-issue-terminology-refresh-m3')
		? messages.userDoesntHaveEditLayoutPermissionMessageIssueTermRefresh
		: messages.userDoesntHaveEditLayoutPermissionMessage;

	return accessControl.userHasEditLayoutPermission
		? screenNotIsolatedMessageValue
		: userDoesntHaveEditLayoutPermissionMessageValue;
};

export const useOnRenderLayoutItem = ({
	FieldComponent,
	PanelComponent,
	project,
	getIndexAfterUnsortableFields,
	highlightRegExp,
}: OnRenderLayoutItemArgs) =>
	useCallback(
		(layoutItem: InactiveLayoutItem, isDragging: boolean, scaleX: number, transition: string) => {
			switch (layoutItem.layoutItemType) {
				case FIELD_LAYOUT_ITEM_TYPE:
					return (
						<FieldComponent
							highlightPattern={highlightRegExp}
							isDragging={isDragging}
							layoutItem={layoutItem}
							scaleX={scaleX}
							transition={transition}
							getIndexAfterUnsortableFields={getIndexAfterUnsortableFields}
						/>
					);
				case PANEL_LAYOUT_ITEM_TYPE:
					return (
						<PanelComponent
							isDragging={isDragging}
							layoutItem={layoutItem}
							project={project}
							isDescriptionVisible={false}
							getIndexAfterUnsortableFields={getIndexAfterUnsortableFields}
						/>
					);
				default:
					// prettier-ignore
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					(layoutItem as never);
					return null;
			}
		},
		// False positive saying FieldComponent and PanelComponent are unnecessary. https://github.com/facebook/react/issues/18051
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[FieldComponent, PanelComponent, getIndexAfterUnsortableFields, highlightRegExp],
	);
